import { useMemo } from 'react'
import { buildChipsByDay, sortValues } from '../laneLayout'
import { type CalendarSortBy } from '../MonthCalendarGrid/types'
import { type CalendarItem } from '../types'

/** What one day's hover summary reports: its content total, split by group. */
export type DayBreakdown = {
  total: number
  breakdown: Record<string, number>
}

export type DayBreakdownDimension = Exclude<CalendarSortBy, 'none'>

/** What the content with no value for the dimension is called in the tooltip. */
export const breakdownFallback: Readonly<
  Record<DayBreakdownDimension, string>
> = {
  account: 'No account',
  channel: 'No channel',
  label: 'No label',
  moment: 'No moment',
}

/**
 * Catch-all bucket for content with no value for the dimension. Same id the
 * week view's histogram uses, so both tooltips read the same way.
 */
export const NO_GROUP_ID = '__no_group__'

/**
 * A month sorted by nothing still has a story to tell on hover, and channel is
 * the split the week view opens with.
 */
export const toBreakdownDimension = (
  sortBy: CalendarSortBy
): DayBreakdownDimension => (sortBy === 'none' ? 'channel' : sortBy)

/** Counts the content each day holds, keyed `yyyy-MM-dd`. */
export const useDayBreakdown = (
  items: readonly CalendarItem[],
  dimension: DayBreakdownDimension
): ReadonlyMap<string, DayBreakdown> =>
  useMemo(() => {
    const summaries = new Map<string, DayBreakdown>()

    buildChipsByDay(items).forEach((chips, dayKey) => {
      const breakdown: Record<string, number> = {}
      chips.forEach((item) => {
        const groupId = sortValues[dimension](item) ?? NO_GROUP_ID
        breakdown[groupId] = (breakdown[groupId] ?? 0) + 1
      })
      summaries.set(dayKey, { breakdown, total: chips.length })
    })

    return summaries
  }, [dimension, items])
